import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import StagingList from '../components/StagingList';
import DiffView from '../components/DiffView';
import DocumentUpload from '../components/DocumentUpload';
import IngestionStatus from '../components/IngestionStatus';
import { API_BASE_URL, authFetch } from '../config/api';

export interface Proposal {
  id: number;
  target_file_path: string;
  title: string;
  proposed_content: string;
  current_content: string | null;
  source_type: string;
  source_reference: string | null;
  dimhazards_id: number | null;
  tags: string[];
  status: string;
  created_at: string;
  reviewed_at: string | null;
  reviewer_notes: string | null;
  dim_hazards: {
    hazardname: string;
    hazardscategoryname: string | null;
  } | null;
}

const baseUrl = API_BASE_URL;

async function fetchProposals(): Promise<Proposal[]> {
  const res = await authFetch(`${baseUrl}/api/knowledge/staging`, { cache: 'no-store' });
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json() as { ok: boolean; proposals: Proposal[] };
  if (!data.ok) throw new Error('Server responded ok: false');
  return data.proposals;
}

async function approveProposal(id: number): Promise<void> {
  const res = await authFetch(`${baseUrl}/api/knowledge/staging/${id}/approve`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  if (!res.ok) throw new Error(await res.text());
}

async function rejectProposal(id: number): Promise<void> {
  const res = await authFetch(`${baseUrl}/api/knowledge/staging/${id}/reject`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  if (!res.ok) throw new Error(await res.text());
}

async function editProposal(params: { id: number; content: string }): Promise<void> {
  const res = await authFetch(`${baseUrl}/api/knowledge/staging/${params.id}/edit`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ proposed_content: params.content }),
  });
  if (!res.ok) throw new Error(await res.text());
}

type FilterStatus = 'all' | 'pending' | 'approved' | 'rejected' | 'edited';

export default function KnowledgePage() {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('pending');
  const [activeJobId, setActiveJobId] = useState<number | null>(null);
  const [showUpload, setShowUpload] = useState(false);

  const { data: proposals, isLoading, isError, error } = useQuery<Proposal[], Error>(
    ['knowledgeStaging'],
    fetchProposals,
    { refetchInterval: activeJobId ? 5000 : false }
  );

  const approveMut = useMutation(approveProposal, {
    onSuccess: () => {
      queryClient.invalidateQueries(['knowledgeStaging']);
      setSelectedId(null);
    },
  });

  const rejectMut = useMutation(rejectProposal, {
    onSuccess: () => {
      queryClient.invalidateQueries(['knowledgeStaging']);
      setSelectedId(null);
    },
  });

  const editMut = useMutation(editProposal, {
    onSuccess: () => {
      queryClient.invalidateQueries(['knowledgeStaging']);
    },
  });

  const filtered = proposals?.filter(
    p => filterStatus === 'all' || p.status === filterStatus
  );

  const selected = proposals?.find(p => p.id === selectedId) ?? null;

  const pendingCount = proposals?.filter(p => p.status === 'pending').length ?? 0;
  const approvedCount = proposals?.filter(p => p.status === 'approved').length ?? 0;
  const totalCount = proposals?.length ?? 0;

  return (
    <div className="knowledge-browse-page">
      <div className="knowledge-browse-page__header">
        <div>
          <h2 className="knowledge-browse-page__title">Staging</h2>
          <p className="text-muted">
            Review AI-generated proposals before they are written to the Obsidian vault.
          </p>
        </div>
        <div className="knowledge-browse-page__stats" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div className="knowledge-stat">
            <span className="knowledge-stat__value">{pendingCount}</span>
            <span className="knowledge-stat__label">pending</span>
          </div>
          <div className="knowledge-stat">
            <span className="knowledge-stat__value">{approvedCount}</span>
            <span className="knowledge-stat__label">approved</span>
          </div>
          <div className="knowledge-stat">
            <span className="knowledge-stat__value">{totalCount}</span>
            <span className="knowledge-stat__label">total</span>
          </div>
          <button
            type="button"
            className="btn"
            onClick={() => setShowUpload(v => !v)}
          >
            {showUpload ? 'Hide upload' : 'Ingest document'}
          </button>
          <Link to="/knowledge" className="btn">
            ← Knowledge Center
          </Link>
        </div>
      </div>

      {showUpload && (
        <div className="knowledge-upload-section">
          <DocumentUpload
            onJobCreated={(jobId) => {
              setActiveJobId(jobId);
              setTimeout(() => setActiveJobId(null), 300000);
            }}
          />
          <IngestionStatus activeJobId={activeJobId} />
        </div>
      )}

      <div className="knowledge-filters">
        {(['pending', 'edited', 'approved', 'rejected', 'all'] as FilterStatus[]).map(s => (
          <button
            key={s}
            className={`knowledge-filter-btn ${filterStatus === s ? 'knowledge-filter-btn--active' : ''}`}
            onClick={() => { setFilterStatus(s); setSelectedId(null); }}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
            {s !== 'all' && proposals && (
              <span className="knowledge-filter-count">
                {proposals.filter(p => p.status === s).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {isLoading && <div className="loading-state">Loading proposals...</div>}
      {isError && <div className="error-state">Error: {error.message}</div>}

      {filtered && (
        <div className="knowledge-browse-page__layout">
          <StagingList
            proposals={filtered}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />

          <main className="knowledge-browse-page__detail">
            {selected ? (
              <DiffView
                proposal={selected}
                onApprove={() => approveMut.mutate(selected.id)}
                onReject={() => rejectMut.mutate(selected.id)}
                onSaveEdit={(content) => editMut.mutate({ id: selected.id, content })}
                isApproving={approveMut.isLoading}
                isRejecting={rejectMut.isLoading}
              />
            ) : (
              <div className="knowledge-empty-detail knowledge-empty-detail--rich">
                <div className="knowledge-empty-detail__icon" aria-hidden>{'\u{1F4DD}'}</div>
                <h3 className="knowledge-empty-detail__title">Select a proposal to review</h3>
                <p className="knowledge-empty-detail__hint">
                  {filtered.length > 0
                    ? `${filtered.length} ${filtered.length === 1 ? 'proposal' : 'proposals'} in the current filter.`
                    : 'No proposals match the current filter.'}
                </p>
                <ul className="knowledge-empty-detail__tips">
                  <li>Use the status filters above to narrow down proposals.</li>
                  <li>Click a proposal to see the diff against the current note.</li>
                  <li>Approve, edit, or reject — the vault updates on approval.</li>
                </ul>
              </div>
            )}
          </main>
        </div>
      )}
    </div>
  );
}
